export const VELOCITY_BC_MIN_FPS = 50;
export const VELOCITY_BC_MAX_FPS = 5000;
export const VELOCITY_BC_MIN = 0.01;
export const VELOCITY_BC_MAX = 2;
export const VELOCITY_BC_MAX_BANDS = 32;

const NUMBER = "[+-]?(?:\\d+(?:\\.\\d*)?|\\.\\d+)(?:[eE][+-]?\\d+)?";
const PAIR = new RegExp(`^(${NUMBER})\\s*(?:->|→|:|=)\\s*(${NUMBER})$`);

function serializedNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) throw new TypeError("Each velocity and BC must be finite.");
  const rounded = Number(numeric.toPrecision(12));
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

export function parseVelocityBcBands(value) {
  const text = String(value ?? "").trim();
  if (!text) return [];

  const entries = text.split(/[\n;,]+/).map((entry) => entry.trim()).filter(Boolean);
  if (entries.length > VELOCITY_BC_MAX_BANDS) {
    throw new RangeError(`Enter no more than ${VELOCITY_BC_MAX_BANDS} velocity bands.`);
  }

  const bands = entries.map((entry) => {
    const match = entry.match(PAIR);
    if (!match) {
      throw new TypeError("Enter each band as “velocity -> BC”, one band per line.");
    }
    const velocity = Number(match[1]);
    const bc = Number(match[2]);
    if (!Number.isFinite(velocity) || !Number.isFinite(bc)) {
      throw new TypeError("Each velocity and BC must be finite.");
    }
    if (bc < VELOCITY_BC_MIN || bc > VELOCITY_BC_MAX) {
      throw new RangeError(
        `Each ballistic coefficient must be between ${VELOCITY_BC_MIN} and ${VELOCITY_BC_MAX}.`,
      );
    }
    return { velocity, bc };
  });

  if (new Set(bands.map(({ velocity }) => velocity)).size !== bands.length) {
    throw new RangeError("Enter each velocity threshold only once.");
  }
  return bands;
}

export function normalizeVelocityBcBands(bands) {
  const normalized = bands.map(({ velocity, bc }) => ({
    velocity: Number(velocity),
    bc: Number(bc),
  }));
  if (normalized.some(({ velocity }) => (
    !Number.isFinite(velocity)
      || velocity < VELOCITY_BC_MIN_FPS
      || velocity > VELOCITY_BC_MAX_FPS
  ))) {
    throw new RangeError(
      `Each velocity threshold must be between ${VELOCITY_BC_MIN_FPS.toLocaleString("en-US")} and ${VELOCITY_BC_MAX_FPS.toLocaleString("en-US")} ft/s.`,
    );
  }
  if (new Set(normalized.map(({ velocity }) => velocity)).size !== normalized.length) {
    throw new RangeError("Enter each velocity threshold only once.");
  }
  return normalized.sort((left, right) => right.velocity - left.velocity);
}

export function serializeVelocityBcBands(bands) {
  return bands.map(({ velocity, bc }) => (
    `${serializedNumber(velocity)}:${serializedNumber(bc)}`
  )).join(";");
}

export function formatVelocityBcBands(bands, formatVelocity) {
  return bands.map(({ velocity, bc }) => (
    `${formatVelocity(velocity)} → ${serializedNumber(bc)}`
  )).join("\n");
}
